import { RiHome2Line } from "@itandi/itandi-bb-ui";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { ItandiChip } from "@itandi/itandi-bb-ui/ItandiChip";
import type React from "react";
import { useFormContext } from "react-hook-form";
import { RoomBlockPropertySelectV2 } from "~/components/common/deals/show/deal_card/block/RoomBlockPropertySelectV2";
import { Datepicker } from "~/components/common/parts/datepicker/Datepicker";
import type { Deal } from "~/models/Deal";
import type { DealUpdateParam } from "~/models/DealForm";
import { toDate } from "~/utils/DateUtil";
import { onChangeDate } from "~/utils/FormUtil";
import Styles from "./Block.module.scss";

type Props = { deal: Deal };

export const RoomBlock: React.FC<Props> = ({ deal }: Props) => {
	const { register, watch, setValue, formState } =
		useFormContext<DealUpdateParam>();
	const { errors } = formState;
	if (deal === null) return null;
	const preferredDate = watch("preferredDate");
	return (
		<div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormRowLabel}>
					<RiHome2Line className={Styles.BlockIcon} />
					物件情報
				</div>
			</div>
			<RoomBlockPropertySelectV2 deal={deal} />
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>エレベーターの有無</span>
					<span className={Styles.RequiredLabel}>
						<ItandiChip label="必須" />
					</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div className={Styles.InputRadioGroup}>
						<InputRadio
							isInvalid={!!errors.enableElevator}
							label="あり"
							value="true"
							{...register("enableElevator")}
						/>
						<InputRadio
							isInvalid={!!errors.enableElevator}
							label="なし"
							value="false"
							{...register("enableElevator")}
						/>
					</div>
					{errors.enableElevator ? (
						<div className={Styles.ErrorMessage}>
							{errors.enableElevator.message}
						</div>
					) : null}
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>オートロックの有無</span>
					<span className={Styles.RequiredLabel}>
						<ItandiChip label="必須" />
					</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div className={Styles.InputRadioGroup}>
						<InputRadio
							isInvalid={!!errors.enableAutoLock}
							label="あり"
							value="true"
							{...register("enableAutoLock")}
						/>
						<InputRadio
							isInvalid={!!errors.enableAutoLock}
							label="なし"
							value="false"
							{...register("enableAutoLock")}
						/>
					</div>
					{errors.enableAutoLock ? (
						<div className={Styles.ErrorMessage}>
							{errors.enableAutoLock.message}
						</div>
					) : null}
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>鍵の置き場所</span>
					<span className={Styles.RequiredLabel}>
						<ItandiChip label="任意" />
					</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<Input
							isInvalid={!!errors.placeKeyStoring}
							placeholder="例）エントランス横の雨樋（番号：1234）"
							{...register("placeKeyStoring")}
						/>
					</div>
					{errors.placeKeyStoring ? (
						<div className={Styles.ErrorMessage}>
							{errors.placeKeyStoring.message}
						</div>
					) : null}
					<div className={Styles.Description}>
						下記について教えてください。
						<br />
						空室時：鍵を設置した場所
						<br />
						立会依頼時：鍵設置を希望する場所（希望がない場合は「希望なし」とご記載ください）
					</div>
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>無償利用が可能な駐車場</span>
					<span className={Styles.RequiredLabel}>
						<ItandiChip label="任意" />
					</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<Input
							isInvalid={!!errors.freeParking}
							placeholder="例）エントランス前"
							{...register("freeParking")}
						/>
					</div>
					{errors.freeParking ? (
						<div className={Styles.ErrorMessage}>
							{errors.freeParking.message}
						</div>
					) : null}
				</div>
			</div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormLabelCol}>
					<span className={Styles.FormLabelText}>入居予定日</span>
					<span className={Styles.RequiredLabel}>
						<ItandiChip label="任意" />
					</span>
				</div>
				<div className={Styles.FormSettingCol}>
					<div>
						<div>
							<Datepicker
								onChange={(date): void => {
									onChangeDate(date, "preferredDate", setValue);
								}}
								selected={toDate(preferredDate)}
							/>
						</div>
					</div>
					<div className={Styles.Description}>
						入居予定日を登録または変更された場合は、施工会社へ速やかにお知らせ頂くことをおすすめします。
					</div>
				</div>
			</div>
		</div>
	);
};
